import { MutationTree }              from 'vuex';
import { cloneDeep, set }            from 'lodash-es';
import { ObjectWithStringKeys }      from '@/types';
import { BackendXAxisSeriesResponseType,
  XAxisReportPanelGroups }                                    from '@/views/x-axis-report/report-edit/helpers/x-axis-report.variables';
import { BuilderConfiguration,
  BuilderConfigurationElement,
  BuilderConfigurationElementOption,
  BuilderElement,
  BuilderElementOption,
  BuilderFunction,
  BuilderGroupContent,
  BuilderGroupDataType,
  GroupDataPayload,
  GroupLoadingPayload,
  XAxisDetails,
  XAxisReportLayout,
  XAxisSeriesResponse }                                    from '@/views/x-axis-report/report-edit/types/types';
import { clearBuilderElements,
  clearBuilderFunctions,
  filterOptionsWithValue,
  findEditedElement,
  getSortedConfiguration,
  setConfigurationOptions }                                    from '@/views/x-axis-report/report-edit/helpers/side-panel/x-axis-report-side-panel.helpers';
import { generateChartLegend,
  generateChartSeries,
  generateXAxis }                                    from '@/views/x-axis-report/report-edit/helpers/chart/x-axis-report-chart.helpers';
import { FILTER_TYPE,
  FilterOption,
  FilterValueRange,
  LeadFilterDetails,
  SelectedFilter,
  SelectedFilterValue }                                    from '@/components/filter-panel/filter-panel.types';
import { generateDefaultValues,
  getFilterComponentType,
  isElementLeadTime,
  isElementNotTypeValue }                                    from '@/views/x-axis-report/report-edit/helpers/x-axis.report-general.helpers';
import { ElementType }               from '@/helpers/variables';
import { findSelectedFilter,
  removeSelectedFiltersFromDisabledFilters }                                    from '@/views/x-axis-report/report-edit/helpers/filters/x-axis-report-filters';
import { StyleConfigurationEvent }   from '@/components/filter-panel/hooks/use-filter-panel-events';
import { OnDropPayload }             from '@/components/drag-and-drop/drag-and-drop.types';
import { CheckboxObject }            from '@/views/events/events.types';
import { ElementOptionStyle }        from '@/components/chart/chart-tooltip/chart-tooltip.types';
import { getCalculateConfiguration } from '@/views/x-axis-report/report-edit/helpers/api-configuration/calculate-configuration';
import { reOrderData, sortByOrder }  from '@/helpers/helpers';

const mutations: MutationTree<XAxisReportLayout> = {
  setGroupLoading(state, payload: GroupLoadingPayload): void {
    set(state, `groups.${payload.group}.isGroupLoading`, payload.loading);
  },
  setChartLoading(state, loading: boolean): void {
    state.isChartLoading = loading;
  },
  setFiltersChanged(state, payload: boolean): void {
    state.hasFiltersChanged = payload;
  },
  setCurrentElementName(state, payload: string): void {
    state.groups.currentElementName = payload;
  },
  setConfiguration(state, configuration: ObjectWithStringKeys<BuilderConfiguration[]>): void {
    state.sidePanelCalculateConfiguration = configuration;
  },
  setDefaultConfiguration(state, configuration: ObjectWithStringKeys<BuilderConfiguration[]>): void {
    state.panelDefaultConfiguration = configuration;
  },
  setLeadElement(state, payload: LeadFilterDetails): void {
    Object.values(XAxisReportPanelGroups)
      .forEach((key: XAxisReportPanelGroups) => {
        (state.groups[ key ].data as BuilderElement[])
          .forEach((element: BuilderElement) => {
            if (key !== XAxisReportPanelGroups.FUNCTIONS) {
              element.leadElement = (element.columnName === payload.name && key === payload.groupName);
            }
          });
      });
  },
  setConfigurationStyle(state, payload: { groupName: XAxisReportPanelGroups; styles: StyleConfigurationEvent }): void {
    const element: BuilderGroupContent | undefined = (state.groups[ payload.groupName ].data as BuilderGroupContent[])
      .find((el: BuilderGroupContent) => el.columnName === payload.styles.filterName);

    if (element) {
      const option: BuilderElementOption | undefined = element.options.find((opt: BuilderElementOption) => opt.name === payload.styles.name);
      if (option) {
        Object.assign(option, {
          ...option,
          color: payload.styles.color ?? option.color,
          label: payload.styles.label ?? option.label,
        });
      }
    } else {
      const customFunction: BuilderGroupContent | undefined = (state.groups[ payload.groupName ].data as BuilderGroupContent[])
        .find((el: BuilderGroupContent) => el.aggregationFunction === payload.styles.filterName);

      if (customFunction) {
        Object.assign(customFunction, {
          ...customFunction,
          color: payload.styles.color ?? customFunction.color,
          label: payload.styles.label ?? customFunction.label,
        });
      }
    }
  },
  setPanelButtonLoading(state, payload: boolean): void {
    state.isPanelButtonLoading = payload;
  },
  setIsPanelOpen(state, value: boolean): void {
    state.isPanelOpen = value;
  },
  setIsChartSaved(state, value: boolean): void {
    state.isChartSaved = value;
  },
  setSaveReportButtonLoading(state, payload: boolean): void {
    state.isSaveReportButtonLoading = payload;
  },
  setActiveGroupName(state, name: XAxisReportPanelGroups): void {
    state.groups.activeGroupName = name;
  },
  clearSelectedFilters(state): void {
    state.disabledFilters = [];
    state.selectedFilters = [];
  },
  setGroupEditMode(state, payload: { groupName: XAxisReportPanelGroups; isEditMode: boolean }): void {
    state.groups[ payload.groupName ].isEditMode = payload.isEditMode;
  },
  setDisabledFilter(state, filterOption: FilterOption): void {
    const filterOptionIndex: number = state.disabledFilters.findIndex((disabledFilter: FilterOption) => (
      disabledFilter.uuid === filterOption.uuid && disabledFilter.valueName === filterOption.valueName
    ));
    const isFilterOptionAlreadyDisabled: boolean = filterOptionIndex !== -1;

    if (isFilterOptionAlreadyDisabled) {
      state.disabledFilters.splice(filterOptionIndex, 1);
    } else {
      state.disabledFilters.push(filterOption);
    }
    state.disabledFilters.sort((a: FilterOption, b: FilterOption) => a.valueName.toString().localeCompare(b.valueName.toString()));
  },
  removeSelectedFiltersFromDisabledFilters(state): void {
    state.disabledFilters = removeSelectedFiltersFromDisabledFilters(state.disabledFilters, state.selectedFilters);
    state.disabledFilters.sort((a: FilterOption, b: FilterOption) => a.valueName.toString().localeCompare(b.valueName.toString()));
  },
  addSingleOptionToSelectedFilter(state, filterOption: FilterOption): void {
    const filter: SelectedFilter | undefined = findSelectedFilter(state.selectedFilters, filterOption.uuid);
    if (filter) {
      switch (filterOption.type) {
      case FILTER_TYPE.CHECKBOX_WITH_OPTIONS:
        filter.values = filter.values.includes(filterOption.valueName) ? [ ...filter.values ] : [ ...filter.values, filterOption.valueName ];
        break;
      case FILTER_TYPE.SEARCH_CHECKBOX_WITH_OPTIONS:
        filter.values = filter.values.includes(filterOption.valueName) ? [ ...filter.values ] : [ ...filter.values, filterOption.valueName ];
        break;
      default:
        break;
      }
    } else {
      const { valueName, ...others } = filterOption;

      if (!isElementLeadTime(filterOption)) {
        switch (filterOption.type) {
        case FILTER_TYPE.CHECKBOX_WITH_OPTIONS:
          others.values = [ valueName ];
          state.selectedFilters.push(cloneDeep(others));
          break;
        case FILTER_TYPE.MULTI_DATE_RANGE: {
          const dateRange: string[] = (valueName as string).split(' - ');
          others.values = [{ min: dateRange[ 0 ], max: dateRange[ 1 ] }];
          state.selectedFilters.push(cloneDeep(others));
          break;
        }
        case FILTER_TYPE.DATE_RANGE:
          others.values = [{ min: valueName.toString(), max: valueName.toString() }];
          state.selectedFilters.push(cloneDeep(others));
          break;
        default:
          break;
        }
      } else {
        state.selectedFilters.push(cloneDeep(others));
      }
    }
    state.selectedFilters.forEach((filters) => filters.values.sort());
  },
  removeSingleOptionFromSelectedFilter(state, filterOption: FilterOption): void {
    const filter: SelectedFilter | undefined = findSelectedFilter(state.selectedFilters, filterOption.uuid);

    if (filter) {
      if (!isElementLeadTime(filter)) {
        switch (filter.type) {
        case FILTER_TYPE.CHECKBOX_WITH_OPTIONS:
          filter.values = filter.values.filter((value: SelectedFilterValue) => value !== filterOption.valueName);
          break;
        case FILTER_TYPE.MULTI_DATE_RANGE:
          filter.values = filter.values.filter((value: SelectedFilterValue) => {
            const valueName = `${(value as FilterValueRange).min} - ${(value as FilterValueRange).max}`;
            return valueName !== filterOption.valueName;
          });
          break;
        case FILTER_TYPE.DATE_RANGE:
          filter.values = filter.values.filter((value: SelectedFilterValue) => (value as FilterValueRange).min !== filterOption.valueName);
          break;
        default:
          break;
        }
      } else {
        filter.values = filter.values.filter((value: SelectedFilterValue) => !(filterOption.valueName as string).includes(value as string));
      }

      if (!filter.values.length) {
        state.selectedFilters = state.selectedFilters.filter((selectedFilter: SelectedFilter) => selectedFilter.uuid !== filter.uuid);
      }
    }
    state.selectedFilters.forEach((filters) => filters.values.sort());
  },
  updateFilterValues(state, filterOption: FilterOption): void {
    const element: BuilderElement | undefined = findEditedElement(state, filterOption.columnName);
    const selectedFilter: SelectedFilter | undefined = findSelectedFilter(state.selectedFilters, filterOption.uuid);

    if (element) {
      if (!isElementLeadTime(filterOption)) {
        switch (element.type) {
        case ElementType.VALUE:
          element.options = element?.options
              ?.map((option: BuilderElementOption) => {
                option.isSelected = selectedFilter?.values.includes(option.name) ?? false;
                return option;
              });
          break;
        case ElementType.DAY:
        case ElementType.DATE_RANGE:
        default:
          element.value = {
            from: (selectedFilter?.values[ 0 ] as FilterValueRange).min ?? '',
            to: (selectedFilter?.values[ 0 ] as FilterValueRange).max ?? '',
          };
          break;
        }
      } else {
        const from: number = Math.abs(+(selectedFilter?.values[ 0 ] as string));
        const to: number = Math.abs(+(selectedFilter?.values[ 1 ] as string));

        element.value = {
          from: Number.isNaN(from) ? '' : from.toString(),
          to: Number.isNaN(to) ? '' : to.toString(),
        };
      }
    }
  },
  setNewFilters(state): void {
    const configuration: ObjectWithStringKeys<BuilderConfiguration[]> = getSortedConfiguration(getCalculateConfiguration(state.groups));

    state.selectedFilters = configuration.filters
      .map((element: BuilderConfiguration) => {
        let values: BuilderConfigurationElementOption[] = [];
        const filter = element as BuilderConfigurationElement;
        const disabledFilterIndex: number = state.disabledFilters.findIndex((disabledFilter: FilterOption) => disabledFilter.columnName === filter.name);

        switch (filter.type) {
        case ElementType.LEAD_TIME:
          values = [ filter.options[ 0 ], filter.options[ filter.options.length - 1 ] ];
          if (disabledFilterIndex >= 0) {
            state.disabledFilters.splice(disabledFilterIndex, 1);
          }
          break;
        case ElementType.RANGE:
        case ElementType.DAY:
          values = filter.options;
          if (disabledFilterIndex >= 0) {
            state.disabledFilters.splice(disabledFilterIndex, 1);
          }
          break;
        case ElementType.VALUE:
        default:
          values = filter.options.map((option) => (option as ElementOptionStyle).name ?? option);
        }

        values.forEach((option: BuilderConfigurationElementOption) => {
          const filterOptionIndex: number = state.disabledFilters.findIndex((disabledFilter: FilterOption) => (
            disabledFilter.columnName === filter.name && disabledFilter.valueName === ((option as ElementOptionStyle).name ?? option)
          ));

          if (filterOptionIndex >= 0) {
            state.disabledFilters.splice(filterOptionIndex, 1);
          }

          return option;
        });

        return {
          columnName: filter.name,
          type: getFilterComponentType(filter),
          uuid: filter.order.toString(),
          values,
        };
      }) as SelectedFilter[];
  },
  setNewReportType(state, lineStyle: BackendXAxisSeriesResponseType): void {
    state.groups[ XAxisReportPanelGroups.FUNCTIONS ].data
      .forEach((fnc: BuilderFunction) => {
        fnc.lineStyle = lineStyle;
      });
  },
  setConfigurationToGroup(state, payload: XAxisDetails): void {
    Object.values(XAxisReportPanelGroups)
      .forEach((groupName: XAxisReportPanelGroups) => {
        state.groups[ groupName ].data = setConfigurationOptions(state.groups[ groupName ], groupName, payload)
          .sort(sortByOrder) as (BuilderElement[] | BuilderFunction[]);
      });
  },
  selectAllOptions(state, payload: { uuid: string; checkAll: boolean }): void {
    const element: BuilderElement | undefined = findEditedElement(state, payload.uuid);

    if (element) {
      element.options = element.options?.map((option: BuilderElementOption) => ({
        ...option,
        isSelected: payload.checkAll,
      })) || [];
    }
  },
  setCheckedOptions(state, payload: { uuid: string; checkedOptions: string[] }): void {
    const element: BuilderElement | undefined = findEditedElement(state, payload.uuid);

    if (element) {
      element.options = element.options?.map((option: BuilderElementOption) => {
        option.isSelected = payload.checkedOptions.includes(option.name);

        return option;
      }) || [];
    }
  },
  setFunctionLineStyle(state, { aggregationFunction, value }): void {
    const functionGroup: BuilderFunction | undefined = state.groups[ XAxisReportPanelGroups.FUNCTIONS ].data
      .find((fnc: BuilderFunction) => fnc.aggregationFunction === aggregationFunction);

    if (functionGroup) {
      functionGroup.lineStyle = value;
    }
  },
  setNewDate(state, payload: { element: BuilderElement; value: { from: string; to: string } }): void {
    const element: BuilderElement | undefined = (state.groups[ state.groups.activeGroupName ].data as BuilderElement[])
      .find((el: BuilderElement) => el.columnName === payload.element.columnName);

    if (element) {
      element.value = payload.value;
    }
  },
  reorderElements(state): void {
    state.groups[ state.groups.activeGroupName ].data = (state.groups[ state.groups.activeGroupName ].data as BuilderElement[])
      .map(reOrderData)
      .map((element: BuilderElement) => ({
        ...element,
        options: element.options?.map(reOrderData) || [],
      }));
  },
  updateDataOrders(state, payload: OnDropPayload<BuilderElement>): void {
    const draggedElement: BuilderElement = (state.groups[ state.groups.activeGroupName ].data as BuilderElement[])
      .splice(payload.from, 1)[ 0 ];

    (state.groups[ state.groups.activeGroupName ].data as BuilderElement[])
      .splice(payload.to, 0, draggedElement);
  },
  updateOptionsOrder(state, payload: OnDropPayload<CheckboxObject>): void {
    const element: BuilderElement | undefined = (state.groups[ state.groups.activeGroupName ].data as BuilderElement[])
      .find((element: BuilderElement) => element.columnName === payload.item.uuid);

    if (element && element.options?.length) {
      const draggedOption: BuilderElementOption | undefined = element.options.splice(payload.from, 1)[ 0 ];

      if (draggedOption) {
        element.options.splice(payload.to, 0, draggedOption);
      }
    }
  },
  updateLeadTimeValue(state, payload: { value: string; isLeadTimeFrom: boolean }): void {
    const leadTimeElement: BuilderElement | undefined = (state.groups[ state.groups.activeGroupName ].data as BuilderElement[])
      .find(isElementLeadTime);

    if (leadTimeElement) {
      leadTimeElement.value = {
        ...leadTimeElement.value,
        [ payload.isLeadTimeFrom ? 'from' : 'to' ]: payload.value,
      };
    }
  },
  clearElements(state): void {
    (state.groups[ state.groups.activeGroupName ].data as BuilderGroupDataType) = (state.groups[ state.groups.activeGroupName ].data as BuilderGroupDataType)
      .map((content: BuilderGroupContent) => {
        if (state.groups.activeGroupName === XAxisReportPanelGroups.FUNCTIONS) {
          return clearBuilderFunctions(content as BuilderFunction);
        }
        return clearBuilderElements(content as BuilderElement);
      });
  },
  setSelectedFunctions(state, uuid: string): void {
    const functionGroup: BuilderFunction | undefined = state.groups[ XAxisReportPanelGroups.FUNCTIONS ].data
      .find((fnc: BuilderFunction) => fnc.aggregationFunction === uuid);

    if (functionGroup) {
      functionGroup.isSelected = !functionGroup.isSelected;
    }
  },
  setChartConfiguration(state, data: XAxisSeriesResponse[]): void {
    state.chartConfiguration = {
      ...state.chartConfiguration,
      legend: {
        ...state.chartConfiguration.legend,
        data: generateChartLegend(data),
      },
      series: generateChartSeries(data),
      xAxis: generateXAxis(state.groups[ XAxisReportPanelGroups.X_AXIS ]),
    };
  },
  setGroupData(state, payload: GroupDataPayload<BuilderGroupDataType>): void {
    const data: BuilderGroupDataType = payload.data
      .map((element: BuilderGroupContent) => {
        if (payload.group !== XAxisReportPanelGroups.FUNCTIONS && isElementNotTypeValue(element as BuilderElement)) {
          return {
            ...element,
            ...generateDefaultValues(),
          };
        }
        return element;
      })
      .filter(filterOptionsWithValue);

    set(state, `groups.${payload.group}.data`, data);
  },
};

export default mutations;
